import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { downloadResource, resourceFileUrl, formatApiErrorDetail } from "@/lib/api";
import { track, getUTM } from "@/lib/analytics";
import { Check, Download } from "lucide-react";

export function ResourceGateModal({ open, onOpenChange, resource }) {
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [fileUrl, setFileUrl] = useState("");

  useEffect(() => {
    if (open) {
      setEmail("");
      setError("");
      setFileUrl("");
    }
  }, [open, resource]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim()) {
      setError("Enter your email.");
      return;
    }
    setBusy(true);
    try {
      const data = await downloadResource({ slug: resource.slug, email, utm: getUTM() });
      track("resource_download", { resource: resource.title, slug: resource.slug });
      const full = resourceFileUrl(data.file_url);
      setFileUrl(full);
      window.open(full, "_blank", "noopener");
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || "Something went wrong.");
    } finally {
      setBusy(false);
    }
  };

  if (!resource) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="resource-gate-modal" className="exec-scope bg-[color:var(--paper)] border-[color:var(--rule)] text-[color:var(--ink)] sm:max-w-md">
        <DialogHeader>
          <p className="exec-eyebrow">{resource.kicker || "Download"}</p>
          <DialogTitle className="font-light text-[clamp(22px,2.4vw,28px)] leading-[1.18] mt-2">{resource.title}</DialogTitle>
          <DialogDescription className="exec-serif text-[15.5px] text-[color:var(--lead-ink)]">
            One email. No pitch. The file opens in a new tab.
          </DialogDescription>
        </DialogHeader>
        {fileUrl ? (
          <div data-testid="resource-gate-success" className="py-2">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-full border border-[color:var(--copper)] flex items-center justify-center"><Check size={16} className="text-[color:var(--copper-deep)]" /></span>
              <p className="exec-serif text-[16px] text-[color:var(--lead-ink)]">Your download is ready.</p>
            </div>
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="exec-btn exec-btn-secondary mt-5" data-testid="resource-gate-download-link">
              <Download size={14} /> Download again
            </a>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 mt-2">
            <input data-testid="resource-gate-email" type="email" className="exec-underline" placeholder="you@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            {error && <p data-testid="resource-gate-error" className="exec-serif text-[16px] text-[color:var(--ink)] border-l-2 border-[color:var(--copper)] pl-3">{error}</p>}
            <button data-testid="resource-gate-submit" type="submit" disabled={busy} className="exec-btn exec-btn-primary w-full disabled:opacity-60">
              {busy ? "\u2026" : "Get the file"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
